import { useIntl, defineMessages, FormattedMessage } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';

import { useFreightQuote } from '../../freight-quote-context';

import style from './shipment-summary.module.less';

const shipmentSummaryMessages = defineMessages({
  panelTitle: {
    id: '8+RBOK',
    description: 'Shipment summary panel title',
    defaultMessage: 'Shipment Summary',
  },
  noQuoteSelected: {
    id: 'DqzPJe',
    description: 'Placeholder text when no quote selected in shipment summary',
    defaultMessage: 'No quote selected',
  },
  billedToLabel: {
    id: 'Kc9VYe',
    description: 'Billing info label in shipment summary panel',
    defaultMessage: 'Billed to your WWEX account',
  },
  invoiceNote: {
    id: '6sI+6v',
    description: 'Invoice note in shipment summary panel',
    defaultMessage: 'An invoice will be sent to your account.',
  },
  freightLineItem: {
    id: 'IbdauR',
    description: 'Freight line item label in shipment summary',
    defaultMessage: 'Freight',
  },
  insuranceLineItem: {
    id: 'TTOs5g',
    description: 'Insurance line item label in shipment summary',
    defaultMessage: 'Insurance',
  },
  totalLineItem: {
    id: '8URJNE',
    description: 'Total line item label in shipment summary',
    defaultMessage: 'Total',
  },
  adjustmentNote: {
    id: '7wgs79',
    description: 'Post-shipment adjustment disclaimer note in shipment summary',
    defaultMessage: 'Final amount may vary. Post-shipment adjustments are common with LTL.',
  },
  estimatedDelivery: {
    id: '61Xbdo',
    description: 'Estimated delivery label in shipment summary quote card',
    defaultMessage: 'Est. delivery: {date}',
  },
  transitDays: {
    id: 'l2FeLq',
    description: 'Transit days label in shipment summary quote card',
    defaultMessage: '{days, plural, one {# day} other {# days}} transit',
  },
});

export const ShipmentSummary = () => {
  const intl = useIntl();
  const { state } = useFreightQuote();
  const selectedQuote = state.selectedQuote;

  const formattedFreight = selectedQuote ? intl.formatNumber(selectedQuote.totalCharges, { style: 'currency', currency: 'USD' }) : '—';
  const formattedInsurance =
    selectedQuote?.insurancePremium != null
      ? intl.formatNumber(selectedQuote.insurancePremium, { style: 'currency', currency: 'USD' })
      : null;
  const formattedTotal = selectedQuote
    ? intl.formatNumber(selectedQuote.totalCharges + (selectedQuote.insurancePremium ?? 0), { style: 'currency', currency: 'USD' })
    : '—';

  return (
    <FlexColumn className={style['shipment-summary']} gap={Gap.Space16}>
      <Text isBold textType={TextType.H4} value={<FormattedMessage {...shipmentSummaryMessages.panelTitle} />} />

      {selectedQuote ? (
        <div className={style['quote-card']}>
          <Text isBold textType={TextType.Paragraph} value={selectedQuote.carrierName} />
          <Text
            textType={TextType.Caption}
            value={intl.formatMessage(shipmentSummaryMessages.estimatedDelivery, {
              date: selectedQuote.estimatedDeliveryDate,
            })}
          />
          <Text
            textType={TextType.Caption}
            value={intl.formatMessage(shipmentSummaryMessages.transitDays, {
              days: selectedQuote.transitDays,
            })}
          />
        </div>
      ) : (
        <div className={style['no-quote-card']}>
          <Text
            className={style['no-quote-text']}
            textType={TextType.Caption}
            value={<FormattedMessage {...shipmentSummaryMessages.noQuoteSelected} />}
          />
        </div>
      )}

      <div className={style['billing-section']}>
        <Text isBold textType={TextType.Caption} value={<FormattedMessage {...shipmentSummaryMessages.billedToLabel} />} />
        <Text
          className={style['invoice-note']}
          textType={TextType.Caption}
          value={<FormattedMessage {...shipmentSummaryMessages.invoiceNote} />}
        />
      </div>

      <FlexColumn className={style['line-items']} gap={Gap.Space8}>
        <FlexRow className={style['line-item']}>
          <Text textType={TextType.Caption} value={<FormattedMessage {...shipmentSummaryMessages.freightLineItem} />} />
          <Text isBold textType={TextType.Caption} value={formattedFreight} />
        </FlexRow>

        {formattedInsurance && (
          <FlexRow className={style['line-item']}>
            <Text textType={TextType.Caption} value={<FormattedMessage {...shipmentSummaryMessages.insuranceLineItem} />} />
            <Text isBold textType={TextType.Caption} value={formattedInsurance} />
          </FlexRow>
        )}

        <div className={style['line-item-divider']} />

        <FlexRow className={style['line-item']}>
          <Text isBold textType={TextType.Caption} value={<FormattedMessage {...shipmentSummaryMessages.totalLineItem} />} />
          <Text isBold textType={TextType.Caption} value={formattedTotal} />
        </FlexRow>
      </FlexColumn>

      <Text
        className={style['adjustment-note']}
        textType={TextType.Caption}
        value={<FormattedMessage {...shipmentSummaryMessages.adjustmentNote} />}
      />
    </FlexColumn>
  );
};
